// SPDX-License-Identifier: AGPL-3.0-only

import type { RouteLocationRaw, RouteRecordName } from "vue-router";
import type { TenantSummary } from "../api/tenants.js";

export interface Breadcrumb {
  readonly label: string;
  readonly to?: RouteLocationRaw;
}

const systemRouteLabels: Readonly<Record<string, string>> = {
  "system-dashboard": "Dashboard",
  "system-tenants": "Tenants",
  "system-users": "Users",
  "system-tiers": "Tiers",
  "system-firmware": "Firmware",
  "system-notifications": "Notifications",
  "system-settings": "Settings",
};

const tenantNotificationTabLabels: Readonly<Record<string, string>> = {
  contacts: "Contacts",
  groups: "Groups",
  templates: "Templates",
  policies: "Policies",
};
const systemNotificationTabLabels: Readonly<Record<string, string>> = {
  defaults: "Defaults",
  templates: "Templates",
};

function routeNameText(routeName: RouteRecordName | null | undefined): string | null {
  if (typeof routeName !== "string") return null;
  return routeName;
}

function systemNotificationTabLabel(
  routeQuery: Readonly<Record<string, unknown>> | undefined,
): string {
  const defaultLabel: string = "Defaults";
  const value: unknown = routeQuery?.tab;
  const tab: unknown = Array.isArray(value) ? value[0] : value;
  return typeof tab === "string"
    ? systemNotificationTabLabels[tab] ?? defaultLabel
    : defaultLabel;
}

function systemBreadcrumbs(
  routeName: string,
  routeQuery: Readonly<Record<string, unknown>> | undefined,
): readonly Breadcrumb[] | null {
  const pageLabel: string | undefined = systemRouteLabels[routeName];
  if (pageLabel === undefined) return null;
  if (routeName === "system-dashboard") return [{ label: "System" }, { label: pageLabel }];
  if (routeName === "system-notifications") {
    return [
      { label: "System", to: { name: "system-dashboard" } },
      { label: pageLabel, to: { name: "system-notifications", query: { tab: "defaults" } } },
      { label: systemNotificationTabLabel(routeQuery) },
    ];
  }
  return [
    { label: "System", to: { name: "system-dashboard" } },
    { label: pageLabel },
  ];
}

function notificationTabLabel(
  routeQuery: Readonly<Record<string, unknown>> | undefined,
): string {
  const defaultLabel: string = "Contacts";
  const value: unknown = routeQuery?.tab;
  const tab: unknown = Array.isArray(value) ? value[0] : value;
  return typeof tab === "string"
    ? tenantNotificationTabLabels[tab] ?? defaultLabel
    : defaultLabel;
}

function tenantBreadcrumbs(
  routeName: string,
  tenant: TenantSummary | null,
  routeQuery: Readonly<Record<string, unknown>> | undefined,
): readonly Breadcrumb[] | null {
  if (tenant === null) return null;
  if (routeName === "tenant-overview") {
    return [{ label: tenant.displayName }, { label: "Overview" }];
  }
  if (routeName === "tenant-dashboard") {
    return [
      { label: tenant.displayName, to: { name: "tenant-overview", params: { tenantId: tenant.id } } },
      { label: "Dashboard" },
    ];
  }
  if (routeName === "tenant-probes") {
    return [
      { label: tenant.displayName, to: { name: "tenant-overview", params: { tenantId: tenant.id } } },
      { label: "Probes" },
    ];
  }
  if (routeName === "tenant-probe-rollout") {
    return [
      { label: tenant.displayName, to: { name: "tenant-overview", params: { tenantId: tenant.id } } },
      { label: "Probes", to: { name: "tenant-probes", params: { tenantId: tenant.id } } },
      { label: "Rollout" },
    ];
  }
  if (routeName === "tenant-members") {
    return [
      { label: tenant.displayName, to: { name: "tenant-overview", params: { tenantId: tenant.id } } },
      { label: "Members" },
    ];
  }
  if (routeName === "tenant-notifications") {
    return [
      { label: tenant.displayName, to: { name: "tenant-overview", params: { tenantId: tenant.id } } },
      { label: "Notifications", to: { name: "tenant-notifications", params: { tenantId: tenant.id }, query: { tab: "contacts" } } },
      { label: notificationTabLabel(routeQuery) },
    ];
  }
  if (routeName === "tenant-probe-configuration") {
    return [
      { label: tenant.displayName, to: { name: "tenant-overview", params: { tenantId: tenant.id } } },
      { label: "Probes", to: { name: "tenant-probes", params: { tenantId: tenant.id } } },
      { label: "Configuration" },
    ];
  }
  return null;
}

export function resolveBreadcrumbs(
  routeName: RouteRecordName | null | undefined,
  tenant: TenantSummary | null,
  routeQuery?: Readonly<Record<string, unknown>>,
): readonly Breadcrumb[] {
  const resolvedRouteName: string | null = routeNameText(routeName);
  if (resolvedRouteName === null) return [{ label: "Access" }];

  const resolvedSystemBreadcrumbs: readonly Breadcrumb[] | null =
    systemBreadcrumbs(resolvedRouteName, routeQuery);
  if (resolvedSystemBreadcrumbs !== null) return resolvedSystemBreadcrumbs;

  if (resolvedRouteName === "no-tenants") return [{ label: "Access" }, { label: "No tenants" }];
  if (resolvedRouteName === "user-settings") return [{ label: "Account" }, { label: "User settings" }];

  const resolvedTenantBreadcrumbs: readonly Breadcrumb[] | null =
    tenantBreadcrumbs(resolvedRouteName, tenant, routeQuery);
  if (resolvedTenantBreadcrumbs !== null) return resolvedTenantBreadcrumbs;

  return [{ label: "Access" }];
}
